import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../utils/api';
import { Heart, Lock, ArrowRight, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const missingToken = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords don\'t match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-8 group">
          <div className="p-2 rounded-xl" style={{ backgroundColor: '#E57A44' }}>
            <Heart className="w-6 h-6" style={{ color: '#FDFBF7', strokeWidth: 2 }} />
          </div>
          <span className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Love Restored</span>
        </Link>

        {missingToken ? (
          <div className="p-8 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #F0EBE0' }} data-testid="reset-missing-token">
            <div className="inline-flex items-center justify-center rounded-full mb-5" style={{ width: 56, height: 56, backgroundColor: '#FEE2E2' }}>
              <AlertTriangle className="w-7 h-7" style={{ color: '#991B1B', strokeWidth: 2 }} />
            </div>
            <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Link is missing a token</h2>
            <p style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', lineHeight: 1.7 }}>
              Please open the link directly from the email we sent you. If your link is old, request a fresh one below.
            </p>
            <Link to="/forgot-password" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }} data-testid="reset-request-new">
              Request a new link
            </Link>
          </div>
        ) : done ? (
          <div className="p-8 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #F0EBE0' }} data-testid="reset-done-panel">
            <div className="inline-flex items-center justify-center rounded-full mb-5" style={{ width: 56, height: 56, backgroundColor: '#78A18E20' }}>
              <CheckCircle2 className="w-8 h-8" style={{ color: '#5C715E', strokeWidth: 2 }} />
            </div>
            <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Password updated</h2>
            <p style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', lineHeight: 1.7 }}>
              You're all set. Redirecting you to the login page…
            </p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="reset-goto-login">
              Go to login now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div>
            <h2 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Set a new password</h2>
            <p className="mb-8" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>Pick something you'll remember — at least 6 characters.</p>

            {error && (
              <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }} data-testid="reset-error">
                <p style={{ fontFamily: 'Manrope, sans-serif', color: '#991B1B', fontSize: '14px' }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500, fontSize: '14px' }}>New password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#8C867D', strokeWidth: 2 }} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2"
                    style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6D5B8', color: '#2C2724' }}
                    placeholder="••••••••"
                    data-testid="reset-password"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500, fontSize: '14px' }}>Confirm new password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#8C867D', strokeWidth: 2 }} />
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2"
                    style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6D5B8', color: '#2C2724' }}
                    placeholder="••••••••"
                    data-testid="reset-confirm"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                data-testid="reset-submit"
              >
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
